import { useEffect, useRef, useState } from "react";

/**
 * Hook unico per la persistenza filtri/stato pagina su sessionStorage.
 *
 * Comportamento:
 *  - Persiste in sessionStorage → cambio pagina = stato preservato
 *  - Chiusura tab/browser = stato AZZERATO automaticamente
 *  - Se l'utente vuole una persistenza definitiva, salva come "Vista" (separato)
 *
 * Uso:
 *   const [filters, setFilters] = usePersistedFilters("clienti", {
 *     q: "", tab: "all", sort: "updated_desc", filters: [], logic: "AND"
 *   });
 *
 *   // Reset esplicito:
 *   const [filters, setFilters, reset] = usePersistedFilters(...);
 *   reset();
 *
 * @param pageKey  Chiave unica per pagina (es. "clienti", "agenda")
 * @param initial  Stato iniziale (oggetto)
 * @returns [state, setState, reset]
 */
export function usePersistedFilters(pageKey, initial = {}) {
  const storageKey = `crm:filters:${pageKey}`;

  // Lazy init: leggi sessionStorage solo al mount
  const [state, setState] = useState(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Merge con initial per garantire shape consistente quando aggiungiamo nuovi campi
        return { ...initial, ...parsed };
      }
    } catch {
      // fallthrough
    }
    return initial;
  });

  // Sync su sessionStorage ad ogni cambio (debounced via microtask)
  const initialRef = useRef(initial);
  useEffect(() => {
    try {
      // Storage solo se diverso da iniziale (evita key vuote)
      if (JSON.stringify(state) === JSON.stringify(initialRef.current)) {
        sessionStorage.removeItem(storageKey);
      } else {
        sessionStorage.setItem(storageKey, JSON.stringify(state));
      }
    } catch {
      // sessionStorage full or disabled: ignora silenziosamente
    }
  }, [state, storageKey]);

  const reset = () => {
    setState(initialRef.current);
    try { sessionStorage.removeItem(storageKey); } catch {}
  };

  return [state, setState, reset];
}

/**
 * Helper: leggi un singolo campo dallo stato persistito (per init lazy).
 * Utile quando si vuole solo un valore senza wrappare tutto in un oggetto.
 */
export function readPersistedField(pageKey, fieldName, defaultValue) {
  try {
    const raw = sessionStorage.getItem(`crm:filters:${pageKey}`);
    if (!raw) return defaultValue;
    const parsed = JSON.parse(raw);
    return parsed?.[fieldName] !== undefined ? parsed[fieldName] : defaultValue;
  } catch {
    return defaultValue;
  }
}
